import { colorPurple300, colorTangerine300, colorTurquoise300, colorViolet300 } from '@amzn/meridian-tokens/base/color';
import type { FC } from 'react';
import { stylesheet } from 'typestyle';

// Opacity constant for consistent color application
const OPACITY_LIGHT = 0.125; // 12.5% opacity (equivalent to hex '20')

// Color utility function to apply opacity to hex colors
export const applyOpacity = (hexColor: string, opacity: number): string => {
  // Validate that hexColor is exactly 6 characters after the # symbol
  if (!hexColor.startsWith('#') || hexColor.length !== 7) {
    throw new Error(`Invalid hex color format: ${hexColor}. Expected 6-character hex format like #RRGGBB`);
  }

  // Validate that all characters after # are valid hex digits
  const hexDigits = hexColor.slice(1);
  if (!/^[0-9A-Fa-f]{6}$/.test(hexDigits)) {
    throw new Error(`Invalid hex color format: ${hexColor}. Expected 6-character hex format like #RRGGBB`);
  }

  // Convert opacity (0-1) to hex (00-FF)
  const opacityHex = Math.round(opacity * 255)
    .toString(16)
    .padStart(2, '0');
  return `${hexColor}${opacityHex}`;
};

export type Locale = {
  countryCode: string;
  text: string;
};

export type PreviewComponentProps = {
  className?: string;
};

export type PreviewComponent = FC<PreviewComponentProps>;

export type ColorVariant = 'pink' | 'teal' | 'purple' | 'orange';

export const LOCALES = {
  CA_ENGLISH: {
    countryCode: 'CA',
    text: 'CA English',
  },
  CA_FRENCH: {
    countryCode: 'CA',
    text: 'CA French',
  },
  US_ENGLISH: {
    countryCode: 'US',
    text: 'US English',
  },
  US_SPANISH: {
    countryCode: 'US',
    text: 'US Spanish',
  },
} as const;

// Define color constants with Meridian design tokens
// Background colors use light tints that complement the border colors
const THEME_COLORS = {
  orange: {
    backgroundColor: applyOpacity(colorTangerine300, OPACITY_LIGHT),
    borderColor: colorTangerine300,
  },
  pink: {
    backgroundColor: applyOpacity(colorPurple300, OPACITY_LIGHT),
    borderColor: colorPurple300,
  },
  purple: {
    backgroundColor: applyOpacity(colorViolet300, OPACITY_LIGHT),
    borderColor: colorViolet300,
  },
  teal: {
    backgroundColor: applyOpacity(colorTurquoise300, OPACITY_LIGHT),
    borderColor: colorTurquoise300,
  },
} as const;

export const LANGUAGE_COLORS = THEME_COLORS;

export const NO_SHARING_LOCALE_ROWS = [
  {
    id: 'english-row',
    items: [
      {
        colors: LANGUAGE_COLORS.pink,
        locale: LOCALES.US_ENGLISH,
      },
      {
        colors: LANGUAGE_COLORS.purple,
        locale: LOCALES.CA_ENGLISH,
      },
    ],
  },
  {
    id: 'other-languages-row',
    items: [
      {
        colors: LANGUAGE_COLORS.teal,
        locale: LOCALES.US_SPANISH,
      },
      {
        colors: LANGUAGE_COLORS.orange,
        locale: LOCALES.CA_FRENCH,
      },
    ],
  },
] as const;

export const css = stylesheet({
  flexSpacer: {
    flexGrow: 1,
  },
  previewContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
});
